import { useState } from "react";
import Api from "../../../shared/api/api";

const INITIAL_FORM_DATA = {
  fourHId: "",
  nameFirst: "",
  nameMiddle: "",
  nameLast: "",
  email: "",
  participant: true,
  admin: false,
  leader: false,
  coordinator: false,
  team: "",
  level: "",
};

const ROLE_FIELDS = [
  "participant",
  "admin",
  "leader",
  "coordinator",
];

export default function AddUserForm({
  users = [],
  setUsers,
  onUserAdded,
}) {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [status, setStatus] = useState("");
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedRole =
    ROLE_FIELDS.find((role) => formData[role]) || "participant";

  const handleInputChange = (field, value) => {
    setStatus("");
    setIsError(false);

    if (field === "role") {
      setFormData((currentData) => ({
        ...currentData,
        participant: value === "participant",
        admin: value === "admin",
        leader: value === "leader",
        coordinator: value === "coordinator",
      }));

      return;
    }

    setFormData((currentData) => ({
      ...currentData,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedData = {
      ...formData,
      fourHId: formData.fourHId.trim(),
      nameFirst: formData.nameFirst.trim(),
      nameMiddle: formData.nameMiddle.trim(),
      nameLast: formData.nameLast.trim(),
      email: formData.email.trim(),
      team: formData.team.trim(),
      level: formData.level.trim(),
    };

    if (
      !normalizedData.fourHId ||
      !normalizedData.nameFirst ||
      !normalizedData.nameLast
    ) {
      setIsError(true);
      setStatus("ID, first name, and last name are required.");
      return;
    }

    setIsSubmitting(true);
    setStatus("");
    setIsError(false);

    try {
      const newUser = await Api.addUser(normalizedData);

      if (newUser?.error) {
        throw new Error(newUser.error);
      }

      if (typeof setUsers === "function") {
        setUsers((currentUsers) => [
          ...currentUsers,
          newUser,
        ]);
      }

      if (typeof onUserAdded === "function") {
        onUserAdded(newUser);
      }

      setFormData(INITIAL_FORM_DATA);
      setStatus("The user was added successfully.");
    } catch (error) {
      console.error("Unable to add FourH user:", error);

      setIsError(true);
      setStatus(
        error?.message ||
          "The user could not be added. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="addForm" onSubmit={handleSubmit}>
      <div className="fourHTableWrapper">
        <table className="adminTable">
          <thead>
            <tr>
              <th scope="col">ID</th>
              <th scope="col">First Name</th>
              <th scope="col">Middle Name</th>
              <th scope="col">Last Name</th>
              <th scope="col">Email</th>
              <th scope="col">Role</th>
              <th scope="col">Team</th>
              <th scope="col">Level</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>
                <input
                  type="text"
                  className="userUpdateInputs"
                  value={formData.fourHId}
                  onChange={(event) =>
                    handleInputChange(
                      "fourHId",
                      event.target.value
                    )
                  }
                  placeholder="User ID"
                  aria-label="User ID"
                  required
                />
              </td>

              <td>
                <input
                  type="text"
                  className="userUpdateInputs"
                  value={formData.nameFirst}
                  onChange={(event) =>
                    handleInputChange(
                      "nameFirst",
                      event.target.value
                    )
                  }
                  placeholder="First Name"
                  aria-label="First name"
                  required
                />
              </td>

              <td>
                <input
                  type="text"
                  className="userUpdateInputs"
                  value={formData.nameMiddle}
                  onChange={(event) =>
                    handleInputChange(
                      "nameMiddle",
                      event.target.value
                    )
                  }
                  placeholder="Middle Name"
                  aria-label="Middle name"
                />
              </td>

              <td>
                <input
                  type="text"
                  className="userUpdateInputs"
                  value={formData.nameLast}
                  onChange={(event) =>
                    handleInputChange(
                      "nameLast",
                      event.target.value
                    )
                  }
                  placeholder="Last Name"
                  aria-label="Last name"
                  required
                />
              </td>

              <td>
                <input
                  type="email"
                  className="userUpdateInputs"
                  value={formData.email}
                  onChange={(event) =>
                    handleInputChange(
                      "email",
                      event.target.value
                    )
                  }
                  placeholder="Email"
                  aria-label="Email address"
                />
              </td>

              <td>
                <select
                  className="usersSelect"
                  value={selectedRole}
                  onChange={(event) =>
                    handleInputChange(
                      "role",
                      event.target.value
                    )
                  }
                  aria-label="User role"
                >
                  <option value="participant">
                    Participant
                  </option>
                  <option value="admin">Admin</option>
                  <option value="leader">Leader</option>
                  <option value="coordinator">
                    Coordinator
                  </option>
                </select>
              </td>

              <td>
                <input
                  type="text"
                  className="userUpdateInputs"
                  value={formData.team}
                  onChange={(event) =>
                    handleInputChange(
                      "team",
                      event.target.value
                    )
                  }
                  placeholder="Team"
                  aria-label="Team"
                />
              </td>

              <td>
                <input
                  type="text"
                  className="userUpdateInputs"
                  value={formData.level}
                  onChange={(event) =>
                    handleInputChange(
                      "level",
                      event.target.value
                    )
                  }
                  placeholder="Level"
                  aria-label="Level"
                />
              </td>

              <td>
                <button
                  className="addBtn"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? "Adding..."
                    : "Add New User"}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {status && (
        <p
          className={`fourHFormStatus${
            isError ? " error" : " success"
          }`}
          role={isError ? "alert" : "status"}
        >
          {status}
        </p>
      )}
    </form>
  );
}